import Cookies from "js-cookie";
import _ from "lodash";
import { authService, logout, useTokens } from "pages/auth";
import React, { ReactNode, createContext, useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Admin, RoleByClient } from "types/admin.type";
import RolesType from "types/roles.type";

interface Props {
  children?: ReactNode;
}

export const AuthContext = createContext({
  currentAdmin: {} as Admin | null,
  setCurrentAdmin: (_admin: Admin | null) => {},
  clientId: "" as string | null,
  setClientId: (_clientId: string | null) => {},
  signOut: () => {},
  isLoading: true,
  isAuthenticated: false,
});

export const AuthProvider = ({ children }: Props) => {
  const navigate = useNavigate();
  const [currentAdmin, setCurrentAdmin] = useState<Admin | null>(null);
  const [clientId, setClientId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  const { accessToken, idToken, userId } = useTokens();

  const fetchAdmin = async () => {
    try {
      if (currentAdmin) return;

      const response = await authService.getCurrentAdmin();

      // Décoder le token pour obtenir emailVerified
      const payload = JSON.parse(atob(idToken!.split(".")[1]));

      // Convertir AdminUser vers Admin
      const adminData: Admin = {
        ...response,
        id: response.id,
        role: response.role as [string],
        roleByClient: response.roleByClient as [RoleByClient],
        emailVerified: payload.email_verified === true,
      };

      setCurrentAdmin(adminData);

      const clientId = _.get(adminData, "roleByClient[0].clientId") as string;
      const roleAdmin = _.get(adminData, "role");
      const isSuperAdmin = roleAdmin?.includes(RolesType.SUPER_ADMIN) || false;
      const savedClientId = Cookies.get("selectedClientId");

      // For super admins, allow using saved client ID, otherwise use assigned client
      const currentClientId = isSuperAdmin ? savedClientId || clientId : clientId;

      setClientId(currentClientId);
    } catch (error) {
      console.error("Error when retrieving the admin role:", error);
      await signOut();
    }
  };

  // Vérifier l'authentification au chargement
  useEffect(() => {
    const checkAuth = async () => {
      if (accessToken && idToken && userId) {
        try {
          // Décoder le token pour vérifier si l'email est vérifié
          const payload = JSON.parse(atob(idToken.split(".")[1]));
          const emailVerified = payload.email_verified === true;

          setIsAuthenticated(true);

          // Si l'email n'est pas vérifié, rediriger vers la page de vérification
          if (!emailVerified) {
            navigate("/authentication/verify-email", {
              state: { email: payload.email },
            });
          } else {
            // Récupérer les données admin complètes
            await fetchAdmin();
          }
        } catch (error) {
          console.error("Error parsing token:", error);
          await signOut();
        }
      } else {
        setIsAuthenticated(false);
      }

      setIsLoading(false);
    };

    checkAuth();
  }, [accessToken, idToken, userId, navigate]);

  const signOut = async () => {
    try {
      // Utiliser la fonction utilitaire pour éviter les appels multiples
      await logout(navigate);
    } catch (error) {
      console.error("Error during logout:", error);
    } finally {
      // Réinitialiser les états locaux
      setCurrentAdmin(null);
      setClientId(null);
      setIsAuthenticated(false);
    }
  };

  const value = {
    currentAdmin,
    setCurrentAdmin,
    clientId,
    setClientId,
    signOut,
    isLoading,
    isAuthenticated,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Hook personnalisé pour utiliser le contexte d'authentification
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return {
    ...context,
    user: context.currentAdmin, // Alias pour compatibilité
  };
};
